import { PreRenderService } from './pre-render.service';
import {Injectable} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgMapApiLoader} from '@ngui/map';
import { Observable, Observer } from 'rxjs';
import LatLngLiteral = google.maps.LatLngLiteral;
import { take } from 'rxjs/operators';
import { UserService } from './user.service';

@Injectable()
export class GoogleMapsService {

  private _geocode: google.maps.Geocoder;
  private _autoComplete: google.maps.places.AutocompleteService;

  // Use this to ensure that anyone using this service has the google API loaded in correctly before usage
  hasLoaded: ReplaySubject<void> = new ReplaySubject();

  constructor(private loader: NgMapApiLoader,
    private userService: UserService,
    private prerenderService: PreRenderService
  ) {
    // ensure google has been externally initialized
    if (!this.prerenderService.isServerMode && !this.userService.isGoogleBot()) {
      loader.load();
      loader.api$.subscribe(() => {
        this._geocode = new google.maps.Geocoder();
        this._autoComplete = new google.maps.places.AutocompleteService();
        this.hasLoaded.next(null);
      });
    }
  }

  /**
   * Retrieve Google Location information based on a LatLng
   * @param latLng
   */
  performReverseGeocode(latLng: LatLngLiteral): Promise<google.maps.GeocoderResult[]> {
    return Observable.create((observer: Observer<google.maps.GeocoderResult[]>) => {
      this.hasLoaded.pipe(take(1)).subscribe(() => {
        this._geocode.geocode({ location: latLng }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) observer.next(results);
            else {
              console.error(results, status);
              observer.next(null);
            }
            observer.complete();
          })
      });
    }).toPromise();
  }

  /**
   * Retrieve Google Location information based on an address
   * @param address
   */
  performGeocode(address: string): Promise<google.maps.GeocoderResult[]> {
    return Observable.create((observer: Observer<google.maps.GeocoderResult[]>) => {
      this.hasLoaded.pipe(take(1)).subscribe(() => {
        this._geocode.geocode(
          {
            address: address,
            componentRestrictions: {
              country: 'ca'
            }
          },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) observer.next(results);
            else {
              console.error(results, status);
              observer.next(null);
            }
            observer.complete();
          })
      });
    }).toPromise();
  }

  getPlacePredictions(searchInput: string, location: google.maps.LatLng, radius: number): Observable<google.maps.places.AutocompletePrediction[]> {
    return Observable.create((observer: Observer<google.maps.places.AutocompletePrediction[]>) => {
      this.hasLoaded.pipe(take(1)).subscribe(() => {
        this._autoComplete.getPlacePredictions({
          input: searchInput,
          componentRestrictions: {country: 'ca'},
          location: location,
          radius: radius
        }, (list, status) => {
          if (!list || status !== google.maps.places.PlacesServiceStatus.OK) {
            observer.next(null);
          } else {
            observer.next(list);
          }
          observer.complete();
        });
      });
    });
  }

  public getLocationTypeFromZoomLevel(zoomLevel: number, results: google.maps.GeocoderResult[]): google.maps.GeocoderResult {
    // TODO: deal with no matching results
    // TODO: fine tune this
    return results.find((r) => {
      const types = r.types;
      // if (zoomLevel >= 17 && (types.indexOf('street_address') >= 0 || types.indexOf('premise') >= 0)) return true;
      if ((zoomLevel >= 16) && (types.indexOf('establishment') >= 0 || types.indexOf('point_of_interest') >= 0)) return true;
      else if (zoomLevel >= 12 && types.indexOf('neighborhood') >= 0) return true;
      else if (zoomLevel >= 10 && types.indexOf('sublocality') >= 0) return true;
      else if (zoomLevel >= 9 && types.indexOf('locality') >= 0) return true;
      else if (types.indexOf('political') >= 0) return true;
      else return false;
    });
  }

}
